import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Anchor, Alert, Breadcrumbs, List, Table } from '@mantine/core';
import { Lightbulb } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function MetaCreativeGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Kreacje reklamowe w Meta Ads"
        description="Jak tworzyć skuteczne reklamy na Facebooku i Instagramie. Formaty, rozmiary obrazów, teksty, CTA."
        path={`${guidesBase}/meta-creatives`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Kreacje reklamowe</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Kreacje reklamowe w Meta Ads</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Kreacja (obraz + tekst) to najważniejszy element reklamy na Facebooku. Dobra kreacja może obniżyć koszt kliknięcia nawet o 50%.</Text>
      </Alert>

      <Stack gap="md">
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Rozmiary obrazów</Text>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Format</Table.Th>
                <Table.Th>Rozmiar</Table.Th>
                <Table.Th>Gdzie wyświetlany</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Kwadrat (1:1)</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">1080 x 1080 px</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Feed Facebook i Instagram</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Poziomy (1.91:1)</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">1200 x 628 px</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Prawa kolumna, linki</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Pionowy (4:5)</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">1080 x 1350 px</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Feed mobilny (najlepszy)</Text></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Stories (9:16)</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">1080 x 1920 px</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">Instagram/Facebook Stories</Text></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed" mt="sm">Droid automatycznie przygotowuje przesłane obrazy w formatach 1:1, 1.91:1 oraz 9:16 (Stories/Reels).</Text>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Elementy reklamy</Text>
          <List size="sm" spacing="xs">
            <List.Item><strong>Tekst główny</strong> (Primary text, max 125 zn.) - wyświetlany nad obrazem. Krótki, konkretny, z wezwaniem do działania.</List.Item>
            <List.Item><strong>Nagłówek</strong> (Headline, max 40 zn.) - pod obrazem, pogrubiony. Najważniejsza korzyść lub oferta.</List.Item>
            <List.Item><strong>Opis</strong> (Description, max 125 zn.) - pod nagłówkiem. Dodatkowe szczegóły.</List.Item>
            <List.Item><strong>Przycisk CTA</strong> - "Dowiedz się więcej", "Kup teraz", "Zarejestruj się" itp.</List.Item>
            <List.Item><strong>URL docelowy</strong> - strona, na którą trafia użytkownik po kliknięciu.</List.Item>
          </List>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Jak Droid pomaga z kreacjami</Text>
          <List size="sm" spacing="xs">
            <List.Item>AI generuje teksty reklam na podstawie analizy Twojej strony</List.Item>
            <List.Item>Automatyczne wyodrębnianie obrazów ze strony (og:image + duże grafiki)</List.Item>
            <List.Item>Przycinanie do wymaganych rozmiarów (kwadrat + poziomy)</List.Item>
            <List.Item>Podgląd reklamy w stylu Facebook Feed przed publikacją</List.Item>
            <List.Item>Edycja kreacji po publikacji bezpośrednio z panelu Droid</List.Item>
          </List>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">Najlepsze praktyki</Text>
          <List size="sm" spacing="xs">
            <List.Item>Używaj jasnych, kontrastowych obrazów z wyraźnym podmiotem</List.Item>
            <List.Item>Ogranicz tekst na obrazie do minimum (zasada 20%)</List.Item>
            <List.Item>Testuj różne kreacje: zdjęcia vs grafiki, różne nagłówki</List.Item>
            <List.Item>Pierwsze 3 sekundy decydują o zatrzymaniu scrollowania</List.Item>
            <List.Item>Dopasuj CTA do celu: "Kup teraz" dla e-commerce, "Dowiedz się więcej" dla usług</List.Item>
          </List>
        </Paper>
      </Stack>
    </Box>
  );
}
